import {gql} from '@apollo/client';
import {getTestProps} from '@benchling/js-test-utils';
import {Color} from '@benchling/nucleus-ui';
import {modals} from '@mantine/modals';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import {DocumentNode} from 'graphql';
import React, {ReactNode, useEffect, useMemo, useRef, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {assert} from 'Asserts';
import {Permissions} from 'auth/PermissionConstants';
import DataConnector from 'connect/DataConnector';
import {MOLBIO_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {MODEL_RUN_SOURCE} from 'constants/AnalyticsConstants';
import SignedExtFileURLProxy from 'external-files/SignedExtFileURLProxy';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {getInsufficientCreditsAgentTypeError} from 'llm/chat-library/MeteringUtils';
import ModelHubBulkBanner from 'model-hub/ModelHubBulkBanner';
import {getScientificAIDisabledMessage} from 'model-hub/permissions/ModelHubPermissions';
import {
  looksLikeNucleotideOnlySequence,
  openDNASequenceWarningModal,
} from 'model-hub/scientific-models/input-forms/structure-prediction/DNASequenceWarningModal';
import StructurePredictionForm from 'model-hub/scientific-models/input-forms/structure-prediction/StructurePredictionForm';
import {NO_MODELS_INSTALLED_BANNER_MESSAGE} from 'model-hub/scientific-models/NoModelsInstalledEmptyState';
import {
  getMinAminoAcidCount,
  getMaxAminoAcidCount,
  getModelDisplayName,
  supportsMSA,
} from 'model-hub/scientific-models/ScientificModelConstants';
import {useAvailableStructurePredictionModels} from 'model-hub/scientific-models/useAvailableScientificModels';
import AaSequenceActions from 'molbio/aa-sequences/AaSequenceActions';
import AaSequenceStore from 'molbio/aa-sequences/AaSequenceStore';
import {ProteinStructureExternalFile, ProteinVersion} from 'molbio/aa-sequences/util/AaSequenceConstants';
import MolstarViewer, {MOLSTAR_PREVIEW_TYPE_TO_FORMAT} from 'molbio/molstar/ViewerWrapper';
import notifications from 'notifications';
import {FONT_PROXIMA_13, FONT_PROXIMA_17} from 'styles/FontConstants';
import {proteinSelectionUIGlobalState} from 'toolbar/aa-sequence/AaSequenceUIGlobalState';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonStyle} from 'ui-library/buttons/Button';
import {ChipSize} from 'ui-library/chips/Chip';
import ChipList from 'ui-library/chips/ChipList';
import {GRAY_DARKER, GRAY_LIGHT} from 'ui-library/Colors';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import legacyRunModal from 'ui-library/legacyRunModal';
import {CenteredSpinner} from 'ui-library/Spinner';

import {
  HasInProgressStructurePredictionJobsQuery,
  HasInProgressStructurePredictionJobsQueryVariables,
} from './__generated/HasInProgressStructurePredictionJobsQuery';
import {RemainingExecutionsAndTenantNameQuery} from './__generated/RemainingExecutionsAndTenantNameQuery';
import {UserVerificationStatusQuery} from './__generated/UserVerificationStatusQuery';
import {AlphaFoldPaperLink} from './AlphaFoldPaperLink';
import {
  AvailableStructureInfo,
  findCoordinateStructureById,
  getStructuresForVersion,
  getCoordinateStructureId,
  ACADEMIC_VERIFICATION_MESSAGE,
  getStructureInfoFromVersions,
  getRunBlockingMessage,
  isAcademicUserBlocked,
  requestRun,
  REMAINING_EXECUTIONS_AND_TENANT_NAME_QUERY,
  USER_VERIFICATION_STATUS,
} from './AlphaFoldTabUtils';
import RedoPredictionModal from './RedoPredictionModal';
import {InProgressPredictionData} from './structure-prediction/CoordinateStructureList';
import {CoordinateStructuresList, MetadataItem} from './structure-prediction/CoordinateStructureList';

export enum ViewState {
  LOADING = 'LOADING',
  ERROR = 'ERROR',
  COORDINATE_STRUCTURES_LIST = 'COORDINATE_STRUCTURES_LIST',
  RUN_STRUCTURE_PREDICTION = 'RUN_STRUCTURE_PREDICTION',
  VIEWER = 'VIEWER',
}

function AlphaFoldCitation({className}: {className?: string}): JSX.Element {
  return (
    <p className={className}>
      3D structures are predicted using&nbsp;
      <AlphaFoldPaperLink />.
    </p>
  );
}

function ViewerPane({
  structureFile,
  structureFileOutOfDate,
  predictionInProgress,
  proteinId,
  folderId,
  structureProteinVersionId,
  aminoAcids,
  permissions,
  selectionStart,
  selectionEnd,
  renderWarningMessage,
  onBack,
  componentIds,
  model,
}: {
  structureFile: ProteinStructureExternalFile | null;
  structureFileOutOfDate: boolean;
  predictionInProgress: boolean;
  proteinId: string;
  folderId: string;
  structureProteinVersionId: number;
  aminoAcids: string;
  permissions: Permissions;
  selectionStart: number;
  selectionEnd: number | null;
  renderWarningMessage: (message: ReactNode) => JSX.Element;
  onBack: () => void;
  componentIds: Array<string>;
  model: string;
}): JSX.Element {
  const userVerificationStatusQueryResult =
    useBenchlingQuery<UserVerificationStatusQuery>(USER_VERIFICATION_STATUS);
  if (userVerificationStatusQueryResult.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else if (userVerificationStatusQueryResult.type === QueryResultType.ERROR) {
    notifications.error('Error when loading structure prediction details. Please refresh the page.');
    return <></>;
  }

  if (structureFile == null) {
    return (
      <div className={css(styles.smallTabSpacing)}>
        <p className={css(styles.largestText)}>Predicting 3D structure...</p>
        <p className={css(styles.smallerText)}>
          This could take some time. You will receive an email when it’s ready.
        </p>
        <AlphaFoldCitation className={css(styles.smallerText)} />
      </div>
    );
  } else {
    const currentUser = userVerificationStatusQueryResult.data.currentUser;
    assert(
      currentUser?.__typename === 'UserNode' || currentUser == null,
      `Non-user should not access this page.`
    );

    let warningMessage: ReactNode | null = null;
    if (structureFileOutOfDate) {
      warningMessage = 'This structure is out of sync.';
      if (predictionInProgress) {
        // NOTE: this message doesn't handle the case where a different user requested the run.
        warningMessage = `${warningMessage} You’ll receive an email when your updated structure is ready.`;
      } else if (getRunBlockingMessage(aminoAcids, permissions, 1, model)) {
        warningMessage = `${warningMessage} ${getRunBlockingMessage(aminoAcids, permissions, 1, model)}`;
      } else if (isAcademicUserBlocked(currentUser)) {
        warningMessage = `${warningMessage} ${ACADEMIC_VERIFICATION_MESSAGE}`;
      } else {
        warningMessage = (
          <span>
            {warningMessage}{' '}
            <Button
              buttonStyle={ButtonStyle.LINK}
              className={css(styles.buttonLinkText)}
              text="Redo structure prediction"
              onClick={() => {
                trackEvent(MOLBIO_ANALYTICS_ACTIONS.CLICKED_ALPHAFOLD_REDO_RUN);
                legacyRunModal(RedoPredictionModal, {proteinId, folderId, previouslyRanModel: model});
              }}
            />
            .
          </span>
        );
      }
    }

    return (
      <div
        className={css(styles.viewerWrapper)}
        {...getTestProps({component: 'AlphaFoldTabView', element: 'viewerWrapper'})}
      >
        <div className={css(styles.topButtonsContainer)}>
          <div
            className={css(styles.backLinkContainer)}
            onClick={onBack}
            {...getTestProps({component: 'AlphaFoldTabView', element: 'backButton'})}
          >
            <BenchlingIcon icon="chevron-left" className={css(styles.backIcon)} />
            <span className={css(styles.backText)}>Back</span>
          </div>
          {componentIds.length > 1 && (
            <MetadataItem
              label="Multimer Components"
              value={
                <ChipList itemIds={componentIds} chipSize={ChipSize.MINI} maxChips={3} isVertical={false} />
              }
              className={styles.multimerComponentsMetadataItem}
            />
          )}
        </div>
        {warningMessage && renderWarningMessage(warningMessage)}
        <SignedExtFileURLProxy
          url={structureFile.url}
          externalFileId={structureFile.id}
          owningEntityApiId={proteinId}
          versionId={structureProteinVersionId}
          renderWithSignedURL={(signedURL: string | null | undefined) => {
            if (signedURL === undefined) {
              return <CenteredSpinner />;
            } else if (signedURL == null) {
              return (
                <InlineBanner bannerType={InlineBannerType.ERROR} text="Error loading structure file." />
              );
            } else {
              return (
                <MolstarViewer
                  url={signedURL}
                  format={MOLSTAR_PREVIEW_TYPE_TO_FORMAT[structureFile.previewType]}
                  className={classNames('reset', css(styles.viewer))}
                  interactionAnalyticsCallback={() => {
                    trackEvent(MOLBIO_ANALYTICS_ACTIONS.INTERACTED_ALPHAFOLD_VIEWER);
                  }}
                  residueStart={selectionStart}
                  residueEnd={selectionEnd}
                  disableSelection={structureFileOutOfDate}
                  currentProteinSequence={aminoAcids}
                />
              );
            }
          }}
        />
      </div>
    );
  }
}

export const HAS_IN_PROGRESS_STRUCTURE_PREDICTION_JOBS: DocumentNode = gql`
  query HasInProgressStructurePredictionJobsQuery($proteinId: StrictString!)
  @skipAudit
  @engComponent(name: MOLBIO) {
    protein(id: $proteinId) {
      ... on ProteinNode {
        id
        inProgressStructurePredictionJobs {
          id
          statusCode
          modelName
          createdAt
          componentApiIds
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export type AlphaFoldTabContentProps =
  | {
      proteinId: string;
      initialView?: undefined;
      clearInitialView?: undefined;
    }
  | {
      proteinId: string;
      initialView: ViewState;
      clearInitialView: () => void;
    };

interface AlphaFoldTabContentEnhancedProps {
  versions?: Array<ProteinVersion>;
  currentlyViewedProteinVersionId: number;
  aminoAcids: string;
  permissions: Permissions;
  currentCoordinateStructureId: string | null;
  folderId: string;
}

class AlphaFoldTabContentConnector extends DataConnector<
  AlphaFoldTabContentProps,
  AlphaFoldTabContentEnhancedProps
> {
  override shouldSubscribeToRedux = false;
  override stores = [AaSequenceStore];

  enhanceProps(): AlphaFoldTabContentEnhancedProps {
    const aaSequenceStore = this.aaSequenceStore();
    const aaSequence = aaSequenceStore.getAaSequenceById(this.props.proteinId)!;
    const currentlyViewedProteinVersionId = aaSequence.version.id;
    return {
      versions: aaSequenceStore.getVersionsById(this.props.proteinId),
      currentlyViewedProteinVersionId,
      aminoAcids: aaSequence.version.aminoAcids,
      permissions: aaSequence.permissions,
      currentCoordinateStructureId: aaSequenceStore.getCoordinateStructureId(currentlyViewedProteinVersionId),
      folderId: aaSequence.folder.api_identifier,
    };
  }
}

function AlphaFoldTab({
  proteinId,
  initialView,
  clearInitialView,
  versions,
  currentlyViewedProteinVersionId,
  aminoAcids,
  permissions,
  currentCoordinateStructureId,
  folderId,
}: AlphaFoldTabContentProps & AlphaFoldTabContentEnhancedProps): JSX.Element {
  const availableModels = useAvailableStructurePredictionModels();

  // Validate that clearInitialView is provided when initialView is set
  assert(
    initialView == null || clearInitialView != null,
    'clearInitialView must be provided when initialView is set to prevent component from resetting to initial view on every re-render'
  );

  const runAllWarningModalId = `alphafold-run-all-dna-warning-${proteinId}`;
  const [proteinSelectionUIState] = proteinSelectionUIGlobalState.useGlobalClientState(proteinId);
  const {start: selectionStart, end: selectionEnd} = proteinSelectionUIState;
  const structureInfo = getStructureInfoFromVersions(currentlyViewedProteinVersionId, versions);
  const [viewState, setViewState] = useState<ViewState>(ViewState.LOADING);
  const [initialLoadCompleted, setInitialLoadCompleted] = useState<boolean>(false);
  const [runAllModelsSubmitting, setRunAllModelsSubmitting] = useState<boolean>(false);
  const runAllModelsInFlightRef = useRef<boolean>(false);

  useEffect(() => {
    modals.close(runAllWarningModalId);
  }, [currentlyViewedProteinVersionId, runAllWarningModalId]);

  const structuresForVersion: Array<AvailableStructureInfo> = useMemo(
    () => getStructuresForVersion(versions, structureInfo.versionId || undefined),
    [versions, structureInfo.versionId]
  );

  const selectedCoordinateStructure = currentCoordinateStructureId
    ? findCoordinateStructureById(structuresForVersion, currentCoordinateStructureId)
    : null;

  const batchQueryResult = useBenchlingQuery<
    HasInProgressStructurePredictionJobsQuery,
    HasInProgressStructurePredictionJobsQueryVariables
  >(HAS_IN_PROGRESS_STRUCTURE_PREDICTION_JOBS, {
    variables: {proteinId},
  });

  const remainingExecutionsQueryResult = useBenchlingQuery<RemainingExecutionsAndTenantNameQuery>(
    REMAINING_EXECUTIONS_AND_TENANT_NAME_QUERY
  );

  const batchQueryData = batchQueryResult.type === QueryResultType.SUCCESS ? batchQueryResult.data : null;

  const inProgressStructurePredictions: Array<InProgressPredictionData> = useMemo(() => {
    if (!batchQueryData || isInaccessible(batchQueryData.protein)) {
      return [];
    }
    return (batchQueryData.protein.inProgressStructurePredictionJobs ?? []).map((job) => ({
      id: job.id,
      model: job.modelName,
      createdAt: job.createdAt,
      componentIds: job.componentApiIds,
    }));
  }, [batchQueryData]);

  const hasPredictionInProgress = inProgressStructurePredictions.length > 0;

  // Capture initialView in a ref so we can use it after clearInitialView() removes it from props.
  // When the workspace tab is first opened, the GraphQL query is still pending when
  // clearInitialView fires, so by the time the query succeeds the prop is already gone.
  const initialViewRef = useRef(initialView);

  // Clear initialView from tab state after consuming it once
  useEffect(() => {
    if (initialView != null && clearInitialView) {
      clearInitialView();
    }
    // Only run once on mount when initialView is present
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Decide what to show upon first render after the query is loaded.
  useEffect(() => {
    if (initialLoadCompleted) {
      return;
    }

    if (batchQueryResult.type === QueryResultType.PENDING) {
      setViewState(ViewState.LOADING);
    } else if (batchQueryResult.type === QueryResultType.ERROR) {
      setInitialLoadCompleted(true);
      setViewState(ViewState.ERROR);
    } else if (batchQueryResult.type === QueryResultType.SUCCESS) {
      setInitialLoadCompleted(true);
      const requestedView = initialView ?? initialViewRef.current;
      if (requestedView != null) {
        setViewState(requestedView);
        initialViewRef.current = undefined;
      } else if (structuresForVersion.length > 0) {
        // If there are any coordinate structures, go directly to viewer
        setViewState(ViewState.VIEWER);
      } else if (hasPredictionInProgress) {
        setViewState(ViewState.COORDINATE_STRUCTURES_LIST);
      } else {
        setViewState(ViewState.RUN_STRUCTURE_PREDICTION);
      }
    }
  }, [
    batchQueryResult.type,
    batchQueryData,
    hasPredictionInProgress,
    initialLoadCompleted,
    structuresForVersion.length,
    initialView,
  ]);

  // Clear current coordinate structure when switching to coordinate structures list view
  useEffect(() => {
    if (viewState === ViewState.COORDINATE_STRUCTURES_LIST) {
      AaSequenceActions.setCoordinateStructureId(currentlyViewedProteinVersionId, null);
    }
  }, [viewState, currentlyViewedProteinVersionId]);

  // Shared handler for running all models
  const executeRunAllModels = async (): Promise<void> => {
    if (remainingExecutionsQueryResult.type !== QueryResultType.SUCCESS) {
      notifications.error('Unable to queue predictions at this time');
      throw new Error('Unable to queue predictions');
    }
    if (availableModels == null) {
      notifications.error('Unable to queue predictions at this time');
      throw new Error('Installed models not yet loaded');
    }

    // Run all available models with just the primary AA sequence ID
    const models = availableModels;
    const sequenceLength = aminoAcids.length;

    // Filter out models where the sequence length is too short or too long
    const invalidModelNames: Array<string> = [];
    const validModels = models.filter((model) => {
      const minCount = getMinAminoAcidCount(model);
      const maxCount = getMaxAminoAcidCount(model);

      if (minCount != null && sequenceLength < minCount) {
        invalidModelNames.push(getModelDisplayName(model));
        return false;
      }
      if (sequenceLength > maxCount) {
        invalidModelNames.push(getModelDisplayName(model));
        return false;
      }
      return true;
    });

    try {
      await Promise.all(
        validModels.map((model) =>
          requestRun(
            [proteinId],
            [],
            proteinId,
            folderId,
            remainingExecutionsQueryResult.data,
            model,
            supportsMSA(model),
            MODEL_RUN_SOURCE.STRUCTURE_TAB
          )
        )
      );
      if (invalidModelNames.length > 0) {
        notifications.warn(
          `The following models were not queued because they are not supported for this sequence length: ${invalidModelNames.join(', ')}`
        );
        notifications.success(`Successfully queued ${validModels.length} prediction models`);
      } else {
        notifications.success('Successfully queued all prediction models');
      }
      batchQueryResult.refetch?.();
    } catch (error) {
      const creditsError = getInsufficientCreditsAgentTypeError(error);
      if (creditsError) {
        if (viewState === ViewState.COORDINATE_STRUCTURES_LIST) {
          notifications.error(`${creditsError.title} ${creditsError.detail}`);
        }
        throw error;
      }
      notifications.error('Failed to queue one or more prediction models');
      throw error;
    }
  };

  const guardedExecuteRunAllModels = async (): Promise<boolean> => {
    if (runAllModelsInFlightRef.current) {
      return false;
    }
    runAllModelsInFlightRef.current = true;
    setRunAllModelsSubmitting(true);
    try {
      await executeRunAllModels();
      return true;
    } finally {
      runAllModelsInFlightRef.current = false;
      setRunAllModelsSubmitting(false);
    }
  };

  const handleRunAllModels = async (): Promise<boolean> => {
    if (runAllModelsInFlightRef.current) {
      return false;
    }
    if (looksLikeNucleotideOnlySequence(aminoAcids)) {
      const shouldProceedWithSubmission = await openDNASequenceWarningModal({modalId: runAllWarningModalId});
      if (!shouldProceedWithSubmission || runAllModelsInFlightRef.current) {
        return false;
      }
    }
    return await guardedExecuteRunAllModels();
  };

  // Render based on viewType
  switch (viewState) {
    case ViewState.LOADING:
      return <CenteredHorizontalThrobber />;

    case ViewState.ERROR:
      notifications.error('Error loading structure prediction details. Please refresh the page.');
      return <></>;

    case ViewState.COORDINATE_STRUCTURES_LIST: {
      // TAC check takes priority, then no-models, then entity permissions
      const tacMessage = getScientificAIDisabledMessage();
      const noModelsMessage =
        availableModels != null && availableModels.length === 0 ? NO_MODELS_INSTALLED_BANNER_MESSAGE : null;
      const entityMessage =
        permissions.structurePredictionEditable === true
          ? null
          : 'You do not have sufficient permissions to create a run on this entity.';
      const disabledMessage = tacMessage ?? noModelsMessage ?? entityMessage ?? undefined;

      return (
        <>
          <ModelHubBulkBanner />
          <CoordinateStructuresList
            coordinateStructures={structuresForVersion}
            onSelectCoordinateStructure={(coordinateStructure) => {
              const coordinateStructureId = getCoordinateStructureId(coordinateStructure);
              AaSequenceActions.setCoordinateStructureId(
                currentlyViewedProteinVersionId,
                coordinateStructureId
              );
              setViewState(ViewState.VIEWER);
            }}
            onCreateRun={() => {
              setViewState(ViewState.RUN_STRUCTURE_PREDICTION);
            }}
            onRunAllModels={handleRunAllModels}
            inProgressStructurePredictions={inProgressStructurePredictions}
            disabledMessage={disabledMessage}
            runAllModelsDisabled={availableModels == null || runAllModelsSubmitting}
          />
        </>
      );
    }

    case ViewState.VIEWER: {
      // Use selected coordinate structure if available, otherwise fall back to structureInfo
      const viewerCoordinateStructure =
        selectedCoordinateStructure ||
        (structureInfo.structureFile
          ? {
              structureFile: structureInfo.structureFile,
              model: structureInfo.model,
              modelVersion: structureInfo.modelVersion,
              versionId: structureInfo.versionId,
              createdAt: structureInfo.createdAt,
              structureFileOutOfDate: structureInfo.structureFileOutOfDate || false,
              componentIds: structureInfo.componentIds,
            }
          : null);

      if (!viewerCoordinateStructure) {
        // Fallback to loading if somehow we get into an inconsistent state
        return <CenteredHorizontalThrobber />;
      }

      return (
        <ViewerPane
          structureFile={viewerCoordinateStructure.structureFile}
          structureFileOutOfDate={viewerCoordinateStructure.structureFileOutOfDate}
          predictionInProgress={hasPredictionInProgress}
          proteinId={proteinId}
          folderId={folderId}
          structureProteinVersionId={viewerCoordinateStructure.versionId}
          aminoAcids={aminoAcids}
          permissions={permissions}
          selectionStart={selectionStart}
          selectionEnd={selectionEnd}
          componentIds={viewerCoordinateStructure.componentIds}
          model={viewerCoordinateStructure.model}
          renderWarningMessage={(message) => (
            <div className={css(styles.smallInlineBannerMargin)}>
              <InlineBanner text={message} bannerType={InlineBannerType.WARNING} />
            </div>
          )}
          onBack={() => {
            AaSequenceActions.setCoordinateStructureId(currentlyViewedProteinVersionId, null);
            setViewState(ViewState.COORDINATE_STRUCTURES_LIST);
          }}
        />
      );
    }

    case ViewState.RUN_STRUCTURE_PREDICTION:
      return (
        <>
          <ModelHubBulkBanner />
          <StructurePredictionForm
            inputProteinId={proteinId}
            folderId={folderId}
            inputAminoAcids={aminoAcids}
            inputPermissions={permissions}
            onSubmit={() => {
              setViewState(ViewState.COORDINATE_STRUCTURES_LIST);
            }}
            onBack={() => {
              setViewState(ViewState.COORDINATE_STRUCTURES_LIST);
            }}
            onRunAllModels={handleRunAllModels}
            source={MODEL_RUN_SOURCE.STRUCTURE_TAB}
          />
        </>
      );
    default:
      return <CenteredHorizontalThrobber />;
  }
}

const styles = StyleSheet.create({
  grayDarker: {
    color: GRAY_DARKER,
  },
  smallTabSpacing: {
    margin: '16px',
  },
  centered: {
    display: 'flex',
    justifyContent: 'center',
  },
  largestText: FONT_PROXIMA_17,
  smallerText: FONT_PROXIMA_13,
  imgMargin: {
    margin: '34px 0px',
  },
  modelSelector: {
    minWidth: '300px',
    textAlign: 'left',
  },
  buttonLinkText: {
    verticalAlign: 'baseline',
  },
  viewerWrapper: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  backLinkContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    cursor: 'pointer',
    color: Color.BLUE_500,
    flexShrink: 0,
    paddingLeft: '8px',
    ':hover': {
      opacity: 0.7,
    },
  },
  backIcon: {
    fontSize: '14px',
  },
  backText: {
    ...FONT_PROXIMA_13,
    fontWeight: 600,
  },
  topButtonsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 12px',
    borderBottom: `1px solid ${GRAY_LIGHT}`,
  },
  viewer: {
    borderWidth: '0px',
    flex: '1 1 auto',
  },
  smallInlineBannerMargin: {
    margin: '4px',
  },
  multimerComponentsMetadataItem: {
    maxWidth: '400px',
  },
});

const ConnectedAlphaFoldContent = AlphaFoldTabContentConnector.connect(AlphaFoldTab);
export default ConnectedAlphaFoldContent;
